import { Injectable } from '@angular/core'
import { Observable } from 'rxjs/Observable'
import { HttpClient, HttpResponse } from '@angular/common/http'
import { environment } from '../../../environments/environment'
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router'
import { BehaviorSubject } from 'rxjs/BehaviorSubject'
import { BillingSubscriptionModel } from '../models/billing/billing-subscription-model'
import { BillingTrialModel } from '../models/billing/billing-trial-model'
import { ResourceLoadingService } from './resource-loading.service'
import { AdministrationModel } from '../models/administration.model'
import { AdministrationService } from './administration.service'


@Injectable()
export class BillingService implements Resolve<Observable<any>> {
  private billing: BillingSubscriptionModel | BillingTrialModel
  public filters: object
  private readonly baseUrl = environment.billing_service_url
  obBillingChanged: BehaviorSubject<BillingSubscriptionModel | BillingTrialModel> = new BehaviorSubject<BillingSubscriptionModel | BillingTrialModel>(undefined)

  /**
   * Constructor.
   *
   * @param _httpClient
   * @param _loadingService
   * @param _administrationService
   */
  constructor(
    private _httpClient: HttpClient,
    private _loadingService: ResourceLoadingService,
    private _administrationService: AdministrationService,
  ) {
    this.filters = {
      paginated: true,
      limit: 10,
      sort: 'date',
      direction: 'DESC',
    }

    // todo: check ownership
    // todo: payload to billing service
    // todo: handle results
  }

  /**
   * Resolve.
   *
   * @param route
   * @param state
   */
  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const administrationId = route.params['administrationId']

    if (administrationId) {
      return this.fetchByAdministrationId(administrationId)
    }

    return Observable.of(null)
  }

  /**
   * Fetch details from billing service.
   *
   * @param administrationModel
   */
  public fetchByAdministration(administrationModel: AdministrationModel): Observable<BillingSubscriptionModel | BillingTrialModel> {
    return this.fetchFromBillingService(administrationModel)
  }

  /**
   * Fetch details from billing service.
   *
   * @param administrationId
   */
  public fetchByAdministrationId(administrationId: number): Observable<BillingSubscriptionModel | BillingTrialModel> {
    return this._administrationService
      .get(administrationId)
      .switchMap(administration => {
        return this.fetchFromBillingService(administration)
      })
  }


  /**
   * Fetch details from billing service.
   *
   * @see https://billing.test.tellow.nl/api/docs
   * @param administrationModel
   */
  private fetchFromBillingService(administrationModel: AdministrationModel): Observable<BillingSubscriptionModel | BillingTrialModel> {
    if (!this._loadingService.isLoading) {
      this._loadingService.loading(true)
    }

    const fetchUrl = `${this.baseUrl}/api/v1/subscriptions/${administrationModel.id}`

    const body = {
      user: {
        id: administrationModel.owner_id,
        role: 'ROLE_OWNER',
        origin: administrationModel.owner_origin || null,
        isBeta: administrationModel.is_beta || false,
      },
      owner: {
        origin: administrationModel.owner_origin || null,
      },
      app: {
        // Send 'web' as platform to simulate as if the end
        // user is using our frontend to get their billing
        // details, when 'ios' is send, the billing service
        // thinks we're on the iOS app and thus require Apple's
        // In-app subscription
        platform: 'web',
        version: '3.25.0',
      },
      createdAt: administrationModel.created_at ? administrationModel.created_at.toISOString() : null,
    }

    return this._httpClient.post<HttpResponse<BillingSubscriptionModel | BillingTrialModel>>(fetchUrl, body, { observe: 'response' }).map(response => {

      // Subscription found
      if (response.status === 200) {
        this.billing = new BillingSubscriptionModel(response.body)
      }

      // Subscription not found, in trial
      if (response.status === 202) {
        this.billing = new BillingTrialModel(response.body)
      }

      // Unauthorized
      if (response.status === 401) {
        console.log(response)
      }

      this.obBillingChanged.next(this.billing)

      if (this._loadingService.isLoading) {
        this._loadingService.loading(false)
      }

      return this.billing
    })
  }
}



// WEBPACK FOOTER //
// ./src/app/main/services/billing.service.ts